/*
  <barra-spedizione> (profumidicasa, tema v17).

  - Nel cassetto: il markup viene ri-renderizzato dal server dopo ogni modifica;
    l'elemento nuovo parte dalla quota precedente (memoria statica) e anima
    fino alla sua. Se il cassetto e' chiuso aspetta `avvia()` dall'apertura.
  - In scheda prodotto: ascolta l'evento pubsub `cartUpdate` di Dawn, legge il
    totale (dal payload o da /cart.js) e aggiorna testo e riempimento sul posto.
  - Nella pagina carrello: cart.js ri-renderizza `.js-contents`, quindi basta
    la memoria per animare dalla quota precedente.
*/
if (!customElements.get('barra-spedizione')) {
  const CHIAVE_MEMORIA = 'pdc-barra-spedizione';

  /* Memoria per contesto, tenuta per la sessione: cambiando pagina la barra
     non riparte da zero, e la spunta non si ridisegna a ogni apertura. */
  const leggiMemoria = () => {
    try {
      return JSON.parse(sessionStorage.getItem(CHIAVE_MEMORIA)) || {};
    } catch (e) {
      return {};
    }
  };
  const scriviMemoria = (memoria) => {
    try {
      sessionStorage.setItem(CHIAVE_MEMORIA, JSON.stringify(memoria));
    } catch (e) {
      /* navigazione privata o storage pieno: si vive senza memoria */
    }
  };

  /* Una sola regione live, persistente nel body: quella dentro il cassetto
     viene sostituita a ogni render e i lettori di schermo non la leggerebbero. */
  const annuncia = (testo) => {
    if (!testo) return;
    let regione = document.getElementById('BarraSpedizione-Annuncio');
    if (!regione) {
      regione = document.createElement('p');
      regione.id = 'BarraSpedizione-Annuncio';
      regione.className = 'visually-hidden';
      regione.setAttribute('role', 'status');
      regione.setAttribute('aria-live', 'polite');
      document.body.appendChild(regione);
    }
    clearTimeout(annuncia.timer);
    regione.textContent = '';
    annuncia.timer = setTimeout(() => {
      regione.textContent = testo;
    }, 150);
  };

  class BarraSpedizione extends HTMLElement {
    static memoria = leggiMemoria();

    constructor() {
      super();
      this.aggiornaDaEvento = this.aggiornaDaEvento.bind(this);
    }

    connectedCallback() {
      this.riempimento = this.querySelector('.barra-spedizione__riempimento');
      this.frase = this.querySelector('.barra-spedizione__frase');
      this.binario = this.querySelector('[role="progressbar"]');
      this.contesto = this.dataset.contesto || 'prodotto';
      this.soglia = parseInt(this.dataset.soglia, 10) || 0;
      this.extra = parseInt(this.dataset.extra, 10) || 0;
      this.totale = parseInt(this.dataset.totale, 10) || 0;

      const prima = BarraSpedizione.memoria[this.contesto];
      const quota = this.quota(this.totale);
      const raggiunta = this.classList.contains('is-raggiunta');
      const cambiata = prima && (Math.abs(prima.quota - quota) > 0.0005 || prima.raggiunta !== raggiunta);
      if (cambiata && this.contesto !== 'prodotto') annuncia(this.testoAttuale());

      if (this.contesto === 'cassetto') {
        const cassetto = this.closest('cart-drawer');
        const aperto = cassetto && cassetto.classList.contains('active');
        const daQuota = prima ? prima.quota : 0;
        const raggiuntaPrima = prima ? prima.raggiunta : false;
        if (prima && !cambiata) {
          // Nulla e' cambiato (es. ricarica pagina): resta ferma dov'e'.
          this.memorizza();
          return;
        }
        if (aperto) {
          this.anima(daQuota, raggiuntaPrima);
        } else {
          this.posiziona(daQuota);
          this.inAttesa = { daQuota, raggiuntaPrima };
        }
        return;
      }

      if (cambiata) {
        this.anima(prima.quota, prima.raggiunta);
      } else {
        this.memorizza();
      }

      if (
        this.contesto !== 'carrello' &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        this.annullaIscrizione = subscribe(PUB_SUB_EVENTS.cartUpdate, this.aggiornaDaEvento);
      }
    }

    disconnectedCallback() {
      if (this.annullaIscrizione) this.annullaIscrizione();
    }

    quota(totale) {
      if (!this.soglia) return 0;
      return Math.min(1, Math.max(0, (totale + this.extra) / this.soglia));
    }

    memorizza() {
      BarraSpedizione.memoria[this.contesto] = {
        quota: Math.round(this.quota(this.totale) * 1000) / 1000,
        raggiunta: this.classList.contains('is-raggiunta'),
      };
      scriviMemoria(BarraSpedizione.memoria);
    }

    testoAttuale() {
      return this.frase ? this.frase.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    /* Mette il riempimento a una quota senza transizione. */
    posiziona(quota) {
      if (!this.riempimento) return;
      this.riempimento.style.transition = 'none';
      this.riempimento.style.transform = `scaleX(${quota})`;
    }

    /* Anima dalla quota data alla propria (CSS: 600 ms ease-out, ritardo 120 ms). */
    anima(daQuota, raggiuntaPrima) {
      if (this.riempimento) {
        this.posiziona(daQuota);
        void this.riempimento.offsetWidth;
        this.riempimento.style.transition = '';
        this.riempimento.style.transform = '';
      }
      if (this.classList.contains('is-raggiunta') && !raggiuntaPrima) this.disegnaSpunta();
      this.memorizza();
    }

    /* Chiamato da cart-drawer.js all'apertura del cassetto. */
    avvia() {
      if (!this.inAttesa) return;
      const { daQuota, raggiuntaPrima } = this.inAttesa;
      this.inAttesa = null;
      this.anima(daQuota, raggiuntaPrima);
    }

    disegnaSpunta() {
      const tratto = this.querySelector('.barra-spedizione__spunta path');
      if (!tratto) return;
      tratto.style.transition = 'none';
      tratto.style.strokeDashoffset = '24';
      void tratto.getBoundingClientRect();
      tratto.style.transition = '';
      tratto.style.strokeDashoffset = '';
    }

    aggiornaDaEvento(evento) {
      const dati = evento && evento.cartData;
      if (dati && typeof dati.total_price === 'number' && Array.isArray(dati.items)) {
        this.aggiorna(dati);
        return;
      }
      const promessa =
        typeof CartItems !== 'undefined' && typeof CartItems.fetchCartData === 'function'
          ? CartItems.fetchCartData()
          : fetch(`${window.routes.cart_url}.json`).then((r) => r.json());
      return promessa.then((carrello) => carrello && this.aggiorna(carrello)).catch(() => {});
    }

    /* Aggiorna sul posto a partire dal JSON del carrello (/cart.js). */
    aggiorna(carrello) {
      const raggiuntaPrima = this.classList.contains('is-raggiunta');
      this.totale = carrello.total_price || 0;
      this.dataset.totale = String(this.totale);

      const conteggio = carrello.item_count || 0;
      const mancante = this.soglia - this.totale - this.extra;
      const raggiunta = mancante <= 0;
      const quota = this.quota(this.totale);

      this.hidden = conteggio > 0 && carrello.requires_shipping === false;
      this.classList.toggle('is-raggiunta', raggiunta);
      if (this.riempimento) this.riempimento.style.setProperty('--barra-quota', quota.toFixed(3));
      if (this.binario) this.binario.setAttribute('aria-valuenow', String(Math.round(quota * 100)));
      this.scriviTesto(mancante, conteggio);
      if (this.binario) this.binario.setAttribute('aria-valuetext', this.testoAttuale());
      if (raggiunta && !raggiuntaPrima) this.disegnaSpunta();
      // In scheda prodotto annuncia solo se non c'e' gia' la barra del cassetto
      // (che si aggiorna nello stesso momento): niente doppio annuncio.
      if (!document.querySelector('barra-spedizione[data-contesto="cassetto"]')) annuncia(this.testoAttuale());
      this.memorizza();
    }

    scriviTesto(mancante, conteggio) {
      if (!this.frase) return;
      const d = this.dataset;
      let testo;
      if (conteggio === 0 && this.extra === 0 && d.testoVuoto) testo = d.testoVuoto;
      else if (mancante <= 0) testo = d.testoRaggiunta || '';
      else testo = d.testoMancano || '';

      this.frase.textContent = '';
      testo.split('[importo]').forEach((parte, indice) => {
        if (indice > 0) {
          const cifra = document.createElement('strong');
          cifra.className = 'barra-spedizione__cifra';
          cifra.textContent = this.formatta(mancante);
          this.frase.appendChild(cifra);
        }
        this.frase.appendChild(document.createTextNode(parte));
      });
    }

    /* Riproduce `money_without_trailing_zeros` sul formato del negozio. */
    formatta(centesimi) {
      const formato = this.dataset.formato || '€{{amount_with_comma_separator}}';
      const valore = centesimi / 100;
      const conSeparatori = (migliaia, decimali, cifre) => {
        const [intero, frazione] = valore.toFixed(cifre).split('.');
        const interoSep = intero.replace(/\B(?=(\d{3})+(?!\d))/g, migliaia);
        return frazione ? `${interoSep}${decimali}${frazione}` : interoSep;
      };
      const senzaZeri = (testo, decimali) => (testo.endsWith(`${decimali}00`) ? testo.slice(0, -3) : testo);

      return formato
        .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, chiave) => {
          switch (chiave) {
            case 'amount_no_decimals':
              return conSeparatori(',', '.', 0);
            case 'amount_with_comma_separator':
              return senzaZeri(conSeparatori('.', ',', 2), ',');
            case 'amount_no_decimals_with_comma_separator':
              return conSeparatori('.', ',', 0);
            case 'amount_with_apostrophe_separator':
              return senzaZeri(conSeparatori("'", '.', 2), '.');
            case 'amount_with_space_separator':
              return senzaZeri(conSeparatori(' ', ',', 2), ',');
            case 'amount_no_decimals_with_space_separator':
              return conSeparatori(' ', ',', 0);
            default:
              return senzaZeri(conSeparatori(',', '.', 2), '.');
          }
        })
        .replace(/<[^>]*>/g, '')
        .trim();
    }
  }

  customElements.define('barra-spedizione', BarraSpedizione);
}
